import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import { stage1AssignmentAPI } from "../../api/client";

import { toast } from "react-toastify";

import "./stage1.css";


export default function Stage1({
  candidates = [],
  offers = [],
  mode = "page",
  onClose,
  onAssigned,
  onRefresh,
}) {
  /*
   * ---------------------------------------------------------
   * STATE
   * ---------------------------------------------------------
   */

  const [selectedCandidates, setSelectedCandidates] =
    useState([]);

  const [showAssignModal, setShowAssignModal] =
    useState(false);

  const [showDelegateModal, setShowDelegateModal] =
    useState(false);

  const [delegateAssignment, setDelegateAssignment] =
    useState(null);

  const [employeeSearch, setEmployeeSearch] =
    useState("");

  const [candidateSearch, setCandidateSearch] =
    useState("");

  const [employees, setEmployees] =
    useState([]);

  const [selectedEmployee, setSelectedEmployee] =
    useState(null);

  const [searchLoading, setSearchLoading] =
    useState(false);

  const [assignLoading, setAssignLoading] =
    useState(false);

  const [delegateLoading, setDelegateLoading] =
    useState(false);

  const [assignments, setAssignments] =
    useState({});

  const [delegationReason, setDelegationReason] =
    useState("");

  // OffersList opens this component in `delegation` mode and
  // passes the pending Stage 1 offers through `offers`.
  // Keep the normal Stage1 page working with `candidates`.
  const stage1Candidates = useMemo(() => {
    if (mode === "delegation") {
      return Array.isArray(offers) ? offers : [];
    }
    return Array.isArray(candidates) ? candidates : [];
  }, [mode, offers, candidates]);


  /*
   * ---------------------------------------------------------
   * CURRENT USER
   * ---------------------------------------------------------
   *
   * Your auth data may be stored in localStorage under
   * different keys. This helper supports the common cases.
   *
   * If your project already has a central auth hook/context,
   * replace this helper with that existing user source.
   */

  const getCurrentUserId = () => {
    try {
      const storedUser =
        localStorage.getItem("user");

      if (storedUser) {
        const user = JSON.parse(storedUser);

        return (
          user?.id ??
          user?.userId ??
          user?.employee_id ??
          user?.employeeId
        );
      }
    } catch (error) {
      console.error(
        "Unable to read current user:",
        error
      );
    }

    return (
      localStorage.getItem("userId") ||
      localStorage.getItem("employeeId") ||
      null
    );
  };

  const currentUserId = getCurrentUserId();

  /*
   * ---------------------------------------------------------
   * ZOHO-SYNCED HEADCOUNT EMPLOYEE HELPERS
   * ---------------------------------------------------------
   *
   * The Zoho sync writes employee records into
   * `headcount_employees`.
   *
   * We read those records through the existing headcount
   * dashboard API. The UI never calls Zoho directly.
   */
  const normalizeHeadcountEmployee = (employee) => {
    const firstName =
      employee?.first_name ||
      employee?.firstName ||
      "";

    const lastName =
      employee?.last_name ||
      employee?.lastName ||
      "";

    const name =
      employee?.name ||
      `${firstName} ${lastName}`.trim() ||
      employee?.email_address ||
      employee?.email ||
      "Employee";

    /*
     * IMPORTANT:
     * id/user_id = application users.id.
     * employee_id = Zoho/headcount employee identifier.
     *
     * Stage 1 assignments use users.id in assigned_to so the
     * existing Hiring Manager board and counts continue to work.
     */
    const userId =
      employee?.user_id ??
      employee?.userId ??
      employee?.id;

    const employeeId =
      employee?.employee_id ??
      employee?.employeeId ??
      employee?.employee_code ??
      "";

    return {
      ...employee,
      id: userId,
      user_id: userId,
      employee_id: employeeId,
      employee_code:
        employee?.employee_code ||
        employeeId ||
        "",
      name,
      email:
        employee?.email ||
        employee?.email_address ||
        "",
      designation:
        employee?.designation ||
        "",
      department:
        employee?.department ||
        "",
      division:
        employee?.division ||
        "",
      business_unit:
        employee?.business_unit ||
        "",
      employee_status:
        employee?.employee_status ||
        employee?.status ||
        "",
    };
  };

  const employeeMatchesSearch = (employee, query) => {
    const value = String(query || "")
      .trim()
      .toLowerCase();

    if (!value) return true;

    return [
      employee?.name,
      employee?.first_name,
      employee?.last_name,
      employee?.email,
      employee?.email_address,
      employee?.employee_id,
      employee?.employee_code,
      employee?.designation,
      employee?.department,
      employee?.division,
      employee?.business_unit,
    ]
      .filter(Boolean)
      .some((field) =>
        String(field).toLowerCase().includes(value)
      );
  };

  const getEmployeeAssignmentId = (employee) => {
    /*
     * Always return users.id for stage1_work_assignment.assigned_to.
     * The synced employee_id is display/search data only.
     */
    return (
      employee?.user_id ??
      employee?.userId ??
      employee?.id
    );
  };


  /*
   * ---------------------------------------------------------
   * CANDIDATE ID HELPER
   * ---------------------------------------------------------
   */

  const getCandidateId = (candidate) => {
    return (
      candidate?.candidate_id ??
      candidate?.id
    );
  };


  /*
   * ---------------------------------------------------------
   * NAME HELPER
   * ---------------------------------------------------------
   */

  const getCandidateName = (candidate) => {
    if (candidate?.candidate_name) {
      return candidate.candidate_name;
    }

    if (candidate?.name) {
      return candidate.name;
    }

    return [
      candidate?.first_name,
      candidate?.last_name,
    ]
      .filter(Boolean)
      .join(" ");
  };


  /*
   * ---------------------------------------------------------
   * SELECT ALL
   * ---------------------------------------------------------
   */

  const allCandidateIds = useMemo(() => {
    return stage1Candidates
      .map(getCandidateId)
      .filter(Boolean);
  }, [stage1Candidates]);


  const allSelected =
    allCandidateIds.length > 0 &&
    allCandidateIds.every((id) =>
      selectedCandidates.includes(id)
    );


  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedCandidates([]);
      return;
    }

    setSelectedCandidates(
      [...allCandidateIds]
    );
  };


  /*
   * ---------------------------------------------------------
   * SELECT ONE
   * ---------------------------------------------------------
   */

  const handleSelectCandidate = (candidateId) => {
    setSelectedCandidates((previous) => {
      if (previous.includes(candidateId)) {
        return previous.filter(
          (id) => id !== candidateId
        );
      }

      return [
        ...previous,
        candidateId,
      ];
    });
  };


  const filteredDelegationCandidates = useMemo(() => {
    if (mode !== "delegation") {
      return stage1Candidates;
    }

    const query = String(candidateSearch || "")
      .trim()
      .toLowerCase();

    if (!query) {
      return stage1Candidates;
    }

    return stage1Candidates.filter((candidate) => {
      return [
        candidate?.candidate_name,
        candidate?.name,
        candidate?.email,
        candidate?.candidate_email,
        candidate?.candidate_id,
        candidate?.id,
        candidate?.position,
        candidate?.position_offered,
        candidate?.job_title,
        candidate?.jobTitle,
      ]
        .filter(Boolean)
        .some((value) =>
          String(value).toLowerCase().includes(query)
        );
    });
  }, [mode, stage1Candidates, candidateSearch]);


  /*
   * ---------------------------------------------------------
   * OPEN DELEGATION WORKSPACE
   * ---------------------------------------------------------
   * OffersList renders this component as the delegation modal.
   * Open the delegation dialog without automatically selecting
   * every pending Stage 1 offer. The user selects the offers
   * manually.
   */
  useEffect(() => {
    if (mode !== "delegation") return;

    // IMPORTANT: opening the delegation modal must NOT select
    // every offer automatically. The recruiter selects offers
    // manually inside the modal.
    setSelectedCandidates([]);
    setCandidateSearch("");
    setEmployeeSearch("");
    setEmployees([]);
    setSelectedEmployee(null);
    setShowAssignModal(true);
  }, [mode]);


  /*
   * ---------------------------------------------------------
   * EMPLOYEE SEARCH
   * ---------------------------------------------------------
   *
   * Search the Zoho-synced `headcount_employees` data.
   *
   * The headcount dashboard endpoint is backed by the
   * headcount_employees table and returns active_employees.
   * No Zoho credentials or direct Zoho calls are made here.
   */
  useEffect(() => {
    const shouldSearch =
      showAssignModal ||
      showDelegateModal;

    if (!shouldSearch) {
      return;
    }

    const query =
      employeeSearch.trim().toLowerCase();

    if (query.length < 2) {
      setEmployees([]);
      return;
    }

    let cancelled = false;

    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);

        /*
         * IMPORTANT:
         * Do NOT call /api/headcount/dashboard here.
         * That endpoint is protected by the full headcount.view
         * permission and is intentionally unavailable to some
         * recruiter roles.
         *
         * Stage 1 delegation already has a dedicated employee
         * search endpoint:
         *
         *   GET /api/stage1-assignment/employees/search?q=...
         *
         * The backend for that endpoint should read the Zoho-synced
         * `headcount_employees` table and return only the employee
         * fields needed by the assignment picker.
         */
        const response =
          await stage1AssignmentAPI.searchEmployees(query);

        if (cancelled) return;

        const rawEmployees =
          Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response?.data?.employees)
              ? response.data.employees
              : Array.isArray(response?.data?.data)
                ? response.data.data
                : [];

        const normalizedEmployees =
          rawEmployees
            .map(normalizeHeadcountEmployee)
            .filter((employee) => {
              /* Never delegate to the currently logged-in employee. */
              if (
                Number(getEmployeeAssignmentId(employee)) ===
                Number(currentUserId)
              ) {
                return false;
              }

              const status =
                String(
                  employee?.employee_status ||
                  employee?.status ||
                  ""
                )
                  .trim()
                  .toLowerCase();

              /*
               * If the endpoint already filters active employees,
               * this remains harmless. If status is absent, keep
               * the record because the endpoint may return a minimal
               * employee projection.
               */
              if (
                status &&
                !["active", "current", "working"].includes(status)
              ) {
                return false;
              }

              return employeeMatchesSearch(
                employee,
                query
              );
            })
            .slice(0, 30);

        setEmployees(normalizedEmployees);
      } catch (error) {
        console.error(
          "Zoho-synced employee search error:",
          error
        );

        if (!cancelled) {
          setEmployees([]);

          toast.error(
            "Unable to load employees from the synced employee table"
          );
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    employeeSearch,
    showAssignModal,
    showDelegateModal,
    currentUserId,
  ]);



  /*
   * ---------------------------------------------------------
   * LOAD EXISTING ASSIGNMENTS
   * ---------------------------------------------------------
   */

  useEffect(() => {
    const loadAssignments = async () => {
      if (!allCandidateIds.length) {
        setAssignments({});
        return;
      }

      try {
        const response =
          await stage1AssignmentAPI
            .getCandidateAssignments(
              allCandidateIds
            );

        const rows =
          Array.isArray(response.data)
            ? response.data
            : [];

        const assignmentMap = {};

        rows.forEach((row) => {
          assignmentMap[row.candidate_id] =
            row;
        });

        setAssignments(assignmentMap);
      } catch (error) {
        console.error(
          "Failed to load assignments:",
          error
        );
      }
    };

    loadAssignments();
  }, [allCandidateIds]);


  /*
   * ---------------------------------------------------------
   * OPEN ASSIGN MODAL
   * ---------------------------------------------------------
   */

  const openAssignModal = () => {
    if (!selectedCandidates.length) {
      toast.warning(
        "Please select at least one candidate"
      );

      return;
    }

    setEmployeeSearch("");
    setEmployees([]);
    setSelectedEmployee(null);

    setShowAssignModal(true);
  };


  /*
   * ---------------------------------------------------------
   * CLOSE ASSIGN MODAL
   * ---------------------------------------------------------
   */

  const closeAssignModal = () => {
    if (assignLoading) {
      return;
    }

    setShowAssignModal(false);
    setEmployeeSearch("");
    setCandidateSearch("");
    setEmployees([]);
    setSelectedEmployee(null);

    if (mode === "delegation" && typeof onClose === "function") {
      onClose();
    }
  };


  /*
   * ---------------------------------------------------------
   * SELECT EMPLOYEE
   * ---------------------------------------------------------
   */

  const handleEmployeeSelect = (employee) => {
    setSelectedEmployee(employee);
  };


  /*
   * ---------------------------------------------------------
   * ASSIGN
   * ---------------------------------------------------------
   */

  const handleAssign = async () => {
    if (!selectedCandidates.length) {
      toast.warning(
        mode === "delegation"
          ? "Please select at least one Stage 1 offer"
          : "Please select at least one candidate"
      );
      return;
    }

    if (!selectedEmployee) {
      toast.warning("Please select an employee");
      return;
    }

    try {
      setAssignLoading(true);

      const targetUserId =
        getEmployeeAssignmentId(selectedEmployee);

      if (!targetUserId) {
        throw new Error(
          "The selected employee is not linked to an application user"
        );
      }

      /*
       * DELEGATION MODE
       *
       * The selected items are candidates/offers, but the
       * delegation API needs the existing stage1_work_assignment IDs.
       *
       * Do NOT call /assign here. The normal assignment endpoint
       * intentionally clears delegation fields.
       */
      if (mode === "delegation") {
        /*
         * Existing assignments use assignmentIds.
         * Pending Stage 1 offers may not have a row in
         * stage1_work_assignment yet, so ALSO send candidateIds.
         *
         * The backend will:
         *   - update an existing assignment when one exists
         *   - create a new delegated assignment when none exists
         */
        const assignmentIds =
          selectedCandidates
            .map(
              (candidateId) =>
                assignments?.[candidateId]?.id
            )
            .filter(Boolean);

        const candidateIds =
          selectedCandidates.map(
            (candidateId) => Number(candidateId)
          );

        const response =
          await stage1AssignmentAPI.delegateMultiple(
            assignmentIds,
            targetUserId,
            delegationReason,
            candidateIds
          );
          console.log("========== DELEGATION DEBUG ==========");
          console.log("Selected employee:", selectedEmployee);
          console.log("Selected employee ID:", selectedEmployee?.id);
          console.log("Selected employee user ID:", selectedEmployee?.user_id);
          console.log("Selected employee employee ID:", selectedEmployee?.employee_id);
          console.log("Assignment:", delegateAssignment);
          console.log("Current user ID:", currentUserId);
          console.log("======================================");

        const data = response.data;

        if (!data?.success) {
          throw new Error(
            data?.message ||
            "Delegation failed"
          );
        }

        toast.success(
          data.message ||
          "Stage 1 work delegated successfully"
        );

        const delegatedByAssignmentId = {};

        (
          data.delegatedAssignments || []
        ).forEach((item) => {
          delegatedByAssignmentId[
            item.assignmentId
          ] = item;
        });

        setAssignments((previous) => {
          const next = {
            ...previous,
          };

          selectedCandidates.forEach(
            (candidateId) => {
              const current =
                previous?.[candidateId];

              if (!current) return;

              const delegated =
                delegatedByAssignmentId[
                  current.id
                ];

              next[candidateId] = {
                ...current,
                ...(delegated || {}),
                assigned_to:
                  targetUserId,
                assigned_to_name:
                  selectedEmployee.name,
                assigned_to_email:
                  selectedEmployee.email,
                assigned_to_employee_code:
                  selectedEmployee.employee_code,
                delegated_from:
                  current.assigned_to,
                delegated_from_name:
                  current.assigned_to_name,
                delegated_from_email:
                  current.assigned_to_email,
                delegated_at:
                  new Date().toISOString(),
                delegation_reason:
                  delegationReason?.trim() ||
                  null,
                status: "Assigned",
              };
            }
          );

          return next;
        });

        setSelectedCandidates([]);
        setDelegationReason("");

        closeAssignModal();

        if (typeof onRefresh === "function") {
          await onRefresh();
        }

        if (
          typeof onAssigned === "function"
        ) {
          await onAssigned();
        }

        return;
      }

      /*
       * NORMAL ASSIGNMENT MODE
       */
      const response =
        await stage1AssignmentAPI.assign(
          selectedCandidates,
          targetUserId
        );

      const data = response.data;

      if (!data?.success) {
        throw new Error(
          data?.message ||
          "Assignment failed"
        );
      }

      toast.success(
        data.message ||
        "Stage 1 work assigned successfully"
      );

      const newAssignments = {
        ...assignments,
      };

      selectedCandidates.forEach(
        (candidateId) => {
          newAssignments[candidateId] = {
            candidate_id: candidateId,
            assigned_to:
              targetUserId,
            assigned_to_name:
              selectedEmployee.name,
            assigned_to_email:
              selectedEmployee.email,
            assigned_to_employee_code:
              selectedEmployee.employee_code,
            delegated_from: null,
            delegated_at: null,
            delegation_reason: null,
            status: "Assigned",
          };
        }
      );

      setAssignments(newAssignments);
      setSelectedCandidates([]);

      closeAssignModal();

      if (typeof onRefresh === "function") {
        await onRefresh();
      }
    } catch (error) {
      console.error(
        "Stage 1 assignment/delegation error:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        (
          mode === "delegation"
            ? "Failed to delegate Stage 1 work"
            : "Failed to assign Stage 1 work"
        )
      );
    } finally {
      setAssignLoading(false);
    }
  };

  /*
   * ---------------------------------------------------------
   * OPEN DELEGATE MODAL
   * ---------------------------------------------------------
   */

  const openDelegateModal = (assignment) => {
    if (!assignment) {
      return;
    }

    /*
     * Only the current assignee should see/use
     * delegation.
     */
    if (
      Number(assignment.assigned_to) !==
      Number(currentUserId)
    ) {
      toast.error(
        "Only the current Stage 1 assignee can delegate this task"
      );

      return;
    }

    /*
     * Don't allow another delegation if already
     * delegated.
     */
    if (assignment.delegated_from !== null &&
        assignment.delegated_from !== undefined) {
      toast.warning(
        "This Stage 1 task has already been delegated"
      );

      return;
    }

    setDelegateAssignment(assignment);
    setEmployeeSearch("");
    setEmployees([]);
    setSelectedEmployee(null);
    setDelegationReason("");
    setShowDelegateModal(true);
  };


  /*
   * ---------------------------------------------------------
   * CLOSE DELEGATE MODAL
   * ---------------------------------------------------------
   */

  const closeDelegateModal = () => {
    if (delegateLoading) {
      return;
    }

    setShowDelegateModal(false);
    setDelegateAssignment(null);
    setEmployeeSearch("");
    setEmployees([]);
    setSelectedEmployee(null);
    setDelegationReason("");
  };


  /*
   * ---------------------------------------------------------
   * DELEGATE
   * ---------------------------------------------------------
   */

  const handleDelegate = async () => {
    if (!delegateAssignment) {
      toast.error(
        "Stage 1 assignment was not selected"
      );

      return;
    }

    if (!selectedEmployee) {
      toast.warning(
        "Please select an employee"
      );

      return;
    }

    if (
      Number(selectedEmployee.id) ===
      Number(currentUserId)
    ) {
      toast.warning(
        "You cannot delegate the task to yourself"
      );

      return;
    }

    try {
      setDelegateLoading(true);

      const response =
        await stage1AssignmentAPI.delegate(
          delegateAssignment.id,
          getEmployeeAssignmentId(selectedEmployee),
          delegationReason
        );

      const data = response.data;

      if (!data?.success) {
        throw new Error(
          data?.message ||
          "Delegation failed"
        );
      }

      toast.success(
        data.message ||
        "Stage 1 work delegated successfully"
      );

      /*
       * Update local assignment.
       */
      const candidateId =
        delegateAssignment.candidate_id;

      setAssignments((previous) => ({
        ...previous,
        [candidateId]: {
          ...previous[candidateId],
          ...(data.assignment || {}),
          candidate_id: candidateId,
        },
      }));

      closeDelegateModal();

      /*
       * Refresh parent data.
       */
      if (typeof onRefresh === "function") {
        await onRefresh();
      }
    } catch (error) {
      console.error(
        "Stage 1 delegation error:",
        error
      );

      toast.error(
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delegate Stage 1 work"
      );
    } finally {
      setDelegateLoading(false);
    }
  };


  /*
   * ---------------------------------------------------------
   * RENDER
   * ---------------------------------------------------------
   */

  return (
    <div className="stage1-page">

      <div className="stage1-header">

        <div>
          <h2>Stage 1</h2>

          <p className="stage1-subtitle">
            Stage 1 candidate approvals
          </p>
        </div>

        <div className="stage1-actions">

          <span className="selected-count">
            {selectedCandidates.length}
            {" "}
            selected
          </span>

          <button
            type="button"
            className="assign-stage1-button"
            disabled={
              selectedCandidates.length === 0
            }
            onClick={openAssignModal}
          >
            Assign Selected
          </button>

        </div>

      </div>


      {/* --------------------------------------------------- */}
      {/* TABLE */}
      {/* --------------------------------------------------- */}

      <div className="stage1-table-wrapper">

        <table className="stage1-table">

          <thead>

            <tr>

              <th scope="col" className="checkbox-column">

                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />

              </th>

              <th scope="col">
                Candidate
              </th>

              <th scope="col">
                Position
              </th>

              <th scope="col">
                Stage
              </th>

              <th scope="col">
                Status
              </th>

              <th scope="col">
                Assigned To
              </th>

              <th scope="col">
                Action
              </th>

            </tr>

          </thead>


          <tbody>

            {stage1Candidates.length === 0 ? (

              <tr>

                <td
                  colSpan="7"
                  className="empty-state"
                >
                  No Stage 1 candidates found
                </td>

              </tr>

            ) : (

              stage1Candidates.map(
                (candidate, index) => {

                  const candidateId =
                    getCandidateId(
                      candidate
                    );

                  const assignment =
                    assignments[
                      candidateId
                    ];

                  const isSelected =
                    selectedCandidates.includes(
                      candidateId
                    );

                  const isCurrentAssignee =
                    assignment &&
                    Number(
                      assignment.assigned_to
                    ) ===
                      Number(currentUserId);

                  const canDelegate =
                    assignment &&
                    assignment.status ===
                      "Assigned" &&
                    isCurrentAssignee &&
                    (assignment.delegated_from ===
                      null ||
                      assignment.delegated_from ===
                        undefined);

                  return (

                    <tr
                      key={`stage1-row-${candidate?.offer_id ?? candidate?.offerId ?? candidateId ?? index}-${index}`}
                      className={
                        isSelected
                          ? "selected-row"
                          : ""
                      }
                    >

                      <td>

                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() =>
                            handleSelectCandidate(
                              candidateId
                            )
                          }
                        />

                      </td>


                      <td>

                        <div className="candidate-name">

                          {getCandidateName(
                            candidate
                          )}

                        </div>

                        {candidate?.email && (

                          <div className="candidate-email">

                            {candidate.email}

                          </div>

                        )}

                      </td>


                      <td>

                        {candidate?.position ||
                          candidate?.job_title ||
                          candidate?.jobTitle ||
                          "-"}

                      </td>


                      <td>

                        <span className="stage-badge">

                          Stage 1

                        </span>

                      </td>


                      <td>

                        {candidate?.status ||
                          candidate?.approval_status ||
                          "Pending"}

                      </td>


                      <td>

                        {assignment ? (

                          <div className="assigned-user">

                            <strong>
                              {
                                assignment
                                  .assigned_to_name
                              }
                            </strong>

                            {assignment
                              .assigned_to_employee_code && (

                              <small>
                                {
                                  assignment
                                    .assigned_to_employee_code
                                }
                              </small>

                            )}

                            {assignment
                              .delegated_from_name && (

                              <span className="delegated-badge">

                                Delegated

                              </span>

                            )}

                          </div>

                        ) : (

                          <span className="not-assigned">

                            Not assigned

                          </span>

                        )}

                      </td>


                      <td>

                        {canDelegate ? (

                          <button
                            type="button"
                            className="delegate-stage1-button"
                            onClick={() =>
                              openDelegateModal(
                                assignment
                              )
                            }
                          >
                            Delegate
                          </button>

                        ) : assignment &&
                          assignment.delegated_from ? (

                          <span className="delegated-text">
                            Delegated
                          </span>

                        ) : (

                          <span className="no-action">
                            -
                          </span>

                        )}

                      </td>

                    </tr>

                  );
                }
              )

            )}

          </tbody>

        </table>

      </div>


      {/* --------------------------------------------------- */}
      {/* ASSIGN MODAL */}
      {/* --------------------------------------------------- */}

      {showAssignModal && (

        <div
          className="stage1-modal-overlay"
          onMouseDown={(event) => {

            if (
              event.target ===
              event.currentTarget
            ) {
              closeAssignModal();
            }

          }}
        >

          <div className="stage1-modal">

            <div className="stage1-modal-header">

              <div>

                <h3>
                  {mode === "delegation" ? "Delegate Stage 1" : "Assign Stage 1 Work"}
                </h3>

                <p>
                  {mode === "delegation"
                    ? "Transfer"
                    : "Assign"}{" "}
                  <strong>
                    {selectedCandidates.length}
                  </strong>{" "}
                  selected candidate
                  {selectedCandidates.length !== 1
                    ? "s"
                    : ""}{" "}
                  to another employee.
                </p>

              </div>


              <button
                type="button"
                className="modal-close-button"
                onClick={closeAssignModal}
                disabled={assignLoading}
              >
                ×
              </button>

            </div>


            <div className="stage1-modal-body">

              {mode === "delegation" && (
                <div className="candidate-search-section">

                  <label>
                    Search Stage 1 Offer / Candidate
                  </label>

                  <input
                    type="text"
                    value={candidateSearch}
                    onChange={(event) =>
                      setCandidateSearch(event.target.value)
                    }
                    placeholder="Search by candidate name, email or position..."
                  />

                </div>
              )}

              {mode === "delegation" && (
                <div className="delegation-candidate-list">

                  {filteredDelegationCandidates.length === 0 ? (
                    <div className="search-message">
                      No Stage 1 offers found
                    </div>
                  ) : (
                    filteredDelegationCandidates.map((candidate, index) => {
                      const candidateId = getCandidateId(candidate);
                      const selected = selectedCandidates.includes(candidateId);
                      const uniqueKey =
                        `delegation-offer-${candidate?.offer_id ?? candidate?.offerId ?? candidateId ?? index}-${index}`;

                      return (
                        <label
                          key={uniqueKey}
                          className={
                            selected
                              ? "delegation-candidate selected"
                              : "delegation-candidate"
                          }
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() =>
                              handleSelectCandidate(candidateId)
                            }
                          />

                          <div>
                            <strong>
                              {getCandidateName(candidate) || "Unnamed Candidate"}
                            </strong>

                            <span>
                              {candidate?.email ||
                                candidate?.candidate_email ||
                                "No email"}
                            </span>

                            <small>
                              {candidate?.position ||
                                candidate?.position_offered ||
                                candidate?.job_title ||
                                candidate?.jobTitle ||
                                ""}
                            </small>
                          </div>
                        </label>
                      );
                    })
                  )}

                </div>
              )}

              <div className="selected-candidates-box">

                <label>
                  Selected Candidates
                </label>

                <div className="selected-candidates-list">

                  {selectedCandidates
                    .slice(0, 5)
                    .map((candidateId) => {

                      const candidate =
                        stage1Candidates.find(
                          (item) =>
                            getCandidateId(
                              item
                            ) === candidateId
                        );

                      return (

                        <span
                          key={`selected-candidate-${candidateId}-${candidate?.offer_id ?? candidate?.id ?? "x"}`}
                          className="candidate-chip"
                        >
                          {candidate
                            ? getCandidateName(
                                candidate
                              )
                            : candidateId}
                        </span>

                      );

                    })}

                  {selectedCandidates.length >
                    5 && (

                    <span className="candidate-chip">

                      +
                      {selectedCandidates.length -
                        5}{" "}
                      more

                    </span>

                  )}

                </div>

              </div>


              <div className="employee-search-section">

                <label>
                  Search Employee
                </label>

                <input
                  type="text"
                  value={employeeSearch}
                  onChange={(event) =>
                    setEmployeeSearch(
                      event.target.value
                    )
                  }
                  placeholder="Search by name, email or employee ID..."
                  autoFocus={mode !== "delegation"}
                />

                {searchLoading && (

                  <div className="search-message">
                    Searching employees...
                  </div>

                )}


                {!searchLoading &&
                  employeeSearch.trim().length >=
                    2 &&
                  employees.length === 0 && (

                    <div className="search-message">
                      No employees found
                    </div>

                  )}


                {employees.length > 0 && (

                  <div className="employee-results">

                    {employees.map(
                      (employee) => {

                        const selected =
                          selectedEmployee?.id ===
                          employee.id;

                        return (

                          <button
                            type="button"
                            key={employee.id}
                            className={
                              selected
                                ? "employee-result selected"
                                : "employee-result"
                            }
                            onClick={() =>
                              handleEmployeeSelect(
                                employee
                              )
                            }
                          >

                            <div className="employee-avatar">

                              {(
                                employee.name ||
                                "E"
                              )
                                .charAt(0)
                                .toUpperCase()}

                            </div>


                            <div className="employee-details">

                              <strong>
                                {
                                  employee.name
                                }
                              </strong>

                              <span>

                                {
                                  employee.employee_code ||
                                  ""
                                }

                                {employee.designation
                                  ? ` • ${employee.designation}`
                                  : ""}

                              </span>

                              {employee.email && (

                                <small>
                                  {
                                    employee.email
                                  }
                                </small>

                              )}

                            </div>


                            <div className="employee-select-mark">

                              {selected
                                ? "✓"
                                : ""}

                            </div>

                          </button>

                        );

                      }
                    )}

                  </div>

                )}

              </div>


              {selectedEmployee && (

                <div className="selected-employee-box">

                  <div>

                    <span>
                      Assigning to
                    </span>

                    <strong>
                      {
                        selectedEmployee.name
                      }
                    </strong>

                  </div>

                  {selectedEmployee.email && (

                    <small>
                      {
                        selectedEmployee.email
                      }
                    </small>

                  )}

                </div>

              )}


              {mode === "delegation" && (
                <div className="delegation-reason-section">

                  <label>
                    Reason
                    <span className="optional-label">
                      {" "}
                      (Optional)
                    </span>
                  </label>

                  <textarea
                    value={delegationReason}
                    onChange={(event) =>
                      setDelegationReason(
                        event.target.value
                      )
                    }
                    placeholder="Enter reason for delegation..."
                    rows={4}
                    maxLength={500}
                  />

                  <div className="reason-counter">
                    {delegationReason.length}/500
                  </div>

                </div>
              )}

              <div className="workflow-info-box">

                <strong>
                  Workflow
                </strong>

                <span>
                  Stage 2 will continue to be
                  determined automatically from
                  the existing Workflow Master.
                </span>

              </div>

            </div>


            <div className="stage1-modal-footer">

              <button
                type="button"
                className="cancel-button"
                onClick={closeAssignModal}
                disabled={assignLoading}
              >
                Cancel
              </button>


              <button
                type="button"
                className="confirm-assign-button"
                onClick={handleAssign}
                disabled={
                  assignLoading ||
                  !selectedEmployee ||
                  selectedCandidates.length ===
                    0
                }
              >

                {assignLoading
                  ? (mode === "delegation"
                    ? "Delegating..."
                    : "Assigning...")
                  : (mode === "delegation"
                    ? "Delegate"
                    : "Assign")}

              </button>

            </div>

          </div>

        </div>

      )}


      {/* --------------------------------------------------- */}
      {/* DELEGATE MODAL */}
      {/* --------------------------------------------------- */}

      {showDelegateModal && delegateAssignment && (

        <div
          className="stage1-modal-overlay"
          onMouseDown={(event) => {

            if (
              event.target ===
              event.currentTarget
            ) {
              closeDelegateModal();
            }

          }}
        >

          <div className="stage1-modal">

            <div className="stage1-modal-header">

              <div>

                <h3>
                  Delegate Stage 1 Work
                </h3>

                <p>
                  Transfer this Stage 1 task to
                  another employee.
                </p>

              </div>

              <button
                type="button"
                className="modal-close-button"
                onClick={closeDelegateModal}
                disabled={delegateLoading}
              >
                ×
              </button>

            </div>


            <div className="stage1-modal-body">

              <div className="workflow-info-box delegate-current-box">

                <strong>
                  Current Assignee
                </strong>

                <span>
                  {delegateAssignment.assigned_to_name ||
                    delegateAssignment.assigned_to_email ||
                    "-"}
                </span>

                {delegateAssignment.assigned_to_email && (
                  <small>
                    {delegateAssignment.assigned_to_email}
                  </small>
                )}

              </div>


              <div className="employee-search-section">

                <label>
                  Delegate To
                </label>

                <input
                  type="text"
                  value={employeeSearch}
                  onChange={(event) =>
                    setEmployeeSearch(
                      event.target.value
                    )
                  }
                  placeholder="Search employee..."
                  autoFocus
                />

                {searchLoading && (

                  <div className="search-message">
                    Searching employees...
                  </div>

                )}


                {!searchLoading &&
                  employeeSearch.trim().length >=
                    2 &&
                  employees.length === 0 && (

                    <div className="search-message">
                      No employees found
                    </div>

                  )}


                {employees.length > 0 && (

                  <div className="employee-results">

                    {employees.map(
                      (employee) => {

                        const selected =
                          selectedEmployee?.id ===
                          employee.id;

                        return (

                          <button
                            type="button"
                            key={employee.id}
                            className={
                              selected
                                ? "employee-result selected"
                                : "employee-result"
                            }
                            onClick={() =>
                              handleEmployeeSelect(
                                employee
                              )
                            }
                          >

                            <div className="employee-avatar">

                              {(
                                employee.name ||
                                "E"
                              )
                                .charAt(0)
                                .toUpperCase()}

                            </div>

                            <div className="employee-details">

                              <strong>
                                {employee.name}
                              </strong>

                              <span>

                                {
                                  employee.employee_code ||
                                  ""
                                }

                                {employee.designation
                                  ? ` • ${employee.designation}`
                                  : ""}

                              </span>

                              {employee.email && (

                                <small>
                                  {employee.email}
                                </small>

                              )}

                            </div>

                            <div className="employee-select-mark">

                              {selected
                                ? "✓"
                                : ""}

                            </div>

                          </button>

                        );

                      }
                    )}

                  </div>

                )}

              </div>


              {selectedEmployee && (

                <div className="selected-employee-box">

                  <div>

                    <span>
                      Delegating To
                    </span>

                    <strong>
                      {selectedEmployee.name}
                    </strong>

                  </div>

                  {selectedEmployee.email && (

                    <small>
                      {selectedEmployee.email}
                    </small>

                  )}

                </div>

              )}


              <div className="delegation-reason-section">

                <label>
                  Reason
                  <span className="optional-label">
                    {" "}
                    (Optional)
                  </span>
                </label>

                <textarea
                  value={delegationReason}
                  onChange={(event) =>
                    setDelegationReason(
                      event.target.value
                    )
                  }
                  placeholder="Enter reason for delegation..."
                  rows={4}
                  maxLength={500}
                />

                <div className="reason-counter">
                  {delegationReason.length}/500
                </div>

              </div>

            </div>


            <div className="stage1-modal-footer">

              <button
                type="button"
                className="cancel-button"
                onClick={closeDelegateModal}
                disabled={delegateLoading}
              >
                Cancel
              </button>

              <button
                type="button"
                className="confirm-delegate-button"
                onClick={handleDelegate}
                disabled={
                  delegateLoading ||
                  !selectedEmployee ||
                  !delegateAssignment
                }
              >

                {delegateLoading
                  ? "Delegating..."
                  : "Delegate"}

              </button>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}